import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  Signal,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { finalize, first } from 'rxjs';

import { UserInfo } from '@blaze-shared/api-contracts-domain';
import { appStateActions } from '@blaze-shared/app-state-data-access';
import { NotificationService } from '@blaze-shared/platform-data-access';
import { ButtonComponent } from '@blaze-shared/shared-ui';

import {
  TwoFactorAuthSetupDialogComponent,
  TwoFactorAuthSetupDialogComponentInput,
  TwoFactorAuthSetupDialogComponentOutput,
} from './two-factor-auth-setup-dialog/two-factor-auth-setup-dialog.component';

@UntilDestroy()
@Component({
  selector: 'blaze-two-factor-auth',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, FormsModule, MatIconModule, MatMenuModule, ReactiveFormsModule],
  templateUrl: './two-factor-auth.component.html',
  styleUrls: ['./two-factor-auth.component.scss'],
})
export class TwoFactorAuthComponent {
  private readonly dialog = inject(MatDialog);
  private readonly notificationService = inject(NotificationService);
  private readonly store = inject<Store<any>>(Store);

  user = input<UserInfo>();
  userHasPasswordLogin = input<boolean>();

  dialogVisible = output<boolean>();
  userUpdated = output<UserInfo>();

  protected userHasTwoFactorAuthEnabled: Signal<boolean>;

  constructor() {
    this.userHasTwoFactorAuthEnabled = computed(() => !!this.user()?.hasTwoFactorAuthEnabled);
  }

  onShowTwoFactorSetupDialog(disableTwoFactorAuth = false): void {
    const user: UserInfo | undefined = this.user();
    if (!user) {
      this.notificationService.error('User information is not available.');
      return;
    }

    this.dialogVisible.emit(true);
    const data: TwoFactorAuthSetupDialogComponentInput = {
      user,
      disableTwoFactorAuth,
    };
    this.dialog
      .open(TwoFactorAuthSetupDialogComponent, {
        data,
        disableClose: false,
        hasBackdrop: true,
        maxWidth: '646px',
        panelClass: ['bd', 'bd-dialog'],
        width: '646px',
      })
      .afterClosed()
      .pipe(
        first(),
        finalize(() => this.dialogVisible.emit(false)),
        untilDestroyed(this),
      )
      .subscribe((dialogOutput: TwoFactorAuthSetupDialogComponentOutput) => {
        if (!dialogOutput?.user) {
          return;
        }
        this.updateUserInfo(dialogOutput.user);
      });
  }

  private updateUserInfo(user: UserInfo): void {
    if (user) {
      this.store.dispatch(appStateActions.updateUserInfo({ user }));
      this.userUpdated.emit(user);
    }
  }
}
